import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppContext } from './app-context';
import { BatchConnection, BatchResponseItem } from './batch-connection';
import { NodeCimOutput, NodeConnection, NodeRequestOptions } from './node-connection';
import { PowerShell, PowerShellSession, PowerShellSessionRequestOptions } from './powershell';
import { PowerShellBatch } from './powershell-batch';
import { PowerShellStream } from './powershell-stream';

/**
 * The PowerShell Alternate processing class.
 */
export class PowerShellAlternate {
    /**
     * The key of powershell session.
     */
    public static readonly cimAlternateKey = 'cimAlt';

    /**
     * Create alternate PowerShell query for CIM query.
     *
     * @param nodeConnection The node connection object.
     * @param nodeName The node name.
     * @param options The node request options.
     * @return Observable<T> if there is no alternate available, return null.
     */
    public static create<T>(
            nodeConnection: NodeConnection,
            nodeName: string,
            options: NodeRequestOptions): Observable<T> {

        if (options == null || options.powerShell == null || options.powerShellContext == null) {
            return null;
        }

        const endpoint = nodeConnection.getJeaEndpoint(nodeName);
        if (endpoint == null && options.powerShellEndpoint == null && !MsftSme.self().Init.powerShellEndpoint) {
            return null;
        }

        const powerShell = PowerShellAlternate.findOrCreateNullPowerShell(nodeName, nodeConnection, options);
        const command = PowerShell.createCommand(options.powerShell, options.powerShellContext.parameters);
        return powerShell.run(command)
            .pipe(map(results => PowerShellAlternate.mapPowerShellToCim(options.powerShellContext.cimOutput, results)));
    }

    /**
     * Create alternate PowerShell Stream query for CIM query.
     *
     * @param nodeConnection The node connection object.
     * @param nodeName The node name.
     * @param options The node request options.
     * @return Observable<T> if there is no alternate available, return null.
     */
    public static createStream<T>(
            powerShellStream: PowerShellStream,
            nodeName: string,
            options: NodeRequestOptions): Observable<T> {
        if (options == null || options.powerShell == null || options.powerShellContext == null) {
            return null;
        }

        const endpoint = powerShellStream.getJeaEndpoint(nodeName);
        if (endpoint == null && options.powerShellEndpoint == null && !MsftSme.self().Init.powerShellEndpoint) {
            return null;
        }

        if (!options.powerShellEndpoint) {
            options.powerShellEndpoint = endpoint;
        }

        const command = PowerShell.createCommand(options.powerShell, options.powerShellContext.parameters);
        return powerShellStream.run(nodeName, command, options)
            .pipe(map(results => PowerShellAlternate.mapPowerShellToCim(options.powerShellContext.cimOutput, results)));
    }

    /**
     * @deprecated
     * (not supported)
     * Create alternate PowerShell Batch query for CIM batch query.
     *
     * @param nodeConnection the node connection object
     * @param batchConnection The batch connection object.
     * @param nodeNames The list of node names.
     * @param options The node request options.
     * @return Observable<BatchResponseItem[]> if there is no alternate available, return null.
     */
    public static createBatch(
            nodeConnection: NodeConnection,
            batchConnection: BatchConnection,
            nodeNames: string[],
            options: NodeRequestOptions): Observable<BatchResponseItem[]> {
        if (options == null || options.powerShell == null || options.powerShellContext == null) {
            return null;
        }

        // TODO: for now we assume all endpoints match. Need to work on a more general solution.
        const node = nodeNames && nodeNames.length > 0 && nodeNames[0];
        const endpoint = nodeConnection.getJeaEndpoint(node);
        if (endpoint == null && options.powerShellEndpoint == null && !MsftSme.self().Init.powerShellEndpoint) {
            return null;
        }

        if (!options.powerShellEndpoint) {
            options.powerShellEndpoint = endpoint;
        }

        const batchSession = new PowerShellBatch(nodeNames, batchConnection, null, null, options);
        const command = PowerShell.createCommand(options.powerShell, options.powerShellContext.parameters);
        return batchSession.runSingleCommand(command, options)
            .pipe(
                map(items => items.map(item =>
                    <BatchResponseItem>{
                        sequenceNumber: item.sequenceNumber,
                        nodeName: item.nodeName,
                        response: {
                            response: PowerShellAlternate.mapPowerShellToCim(options.powerShellContext.cimOutput, item.properties),
                            status: item.status
                        }
                    })));
    }

    /**
     * Reserve a session for all alternate CIM non-batch query.
     *
     * @param nodeName the node name.
     * @param nodeConnection the node connection object.
     * @param options the node request options.
     */
    public static reserveSession(appContext: AppContext, nodeName: string, options?: PowerShellSessionRequestOptions): PowerShellSession {
        return appContext.powerShell.createSession(nodeName, PowerShellAlternate.cimAlternateKey, options);
    }

    /**
     * Dispose the session for alternate CIM non-batch query.
     *
     * @param session the session object.
     */
    public static disposeSession(session: PowerShellSession): void {
        session.dispose();
    }

    /**
     * Find existing powershell session with the fixed key or create null session.
     *
     * @param nodeName the node name.
     * @param nodeConnection the node connection object.
     * @param options the node request options.
     */
    private static findOrCreateNullPowerShell(nodeName: string, nodeConnection: NodeConnection, options: NodeRequestOptions): PowerShell {
        let powerShell = PowerShell.find(nodeName, PowerShellAlternate.cimAlternateKey);
        if (powerShell == null) {
            powerShell = PowerShell.create(nodeName, nodeConnection, null, null, options);
        }

        return powerShell;
    }

    /**
     * Convert the PowerShell results to Cim expected format.
     *
     * @param outputType The output data type.
     * @param data The input data.
     */
    private static mapPowerShellToCim(outputType: NodeCimOutput, data: any): any {
        switch (outputType) {
            case NodeCimOutput.Single:
                // single instance result.
                return {
                    properties: data.results && data.results[0] && PowerShellAlternate.cleanCimInstance(data.results[0])
                };
            case NodeCimOutput.Multiple:
                // multiple instances result.
                return {
                    value: data.results.map(instance => {
                        return {
                            properties: PowerShellAlternate.cleanCimInstance(instance)
                        };
                    })
                };
            case NodeCimOutput.Result:
                return PowerShellAlternate.adjustCimResults(data);

            case NodeCimOutput.Query:
                // query results without properties.
                return {
                    value: data.results.map(instance => {
                        return PowerShellAlternate.cleanCimInstance(instance);
                    })
                };
        }
    }

    /**
     * Adjust PowerShell CIM results data.
     *
     * @param data The result data.
     */
    private static adjustCimResults(data: any): any {
        let response: any = {};

        // invoke method results with free structured but index 0.
        if (!data.results) {
            return response;
        }

        if (data.results.length === 0) {
            return response;
        }

        //
        // Packed the same parameterName object into single array.
        //
        // parameterName
        // itemType: 16: Instance
        // itemValue: Instance Data
        // psComputerName
        //
        const results: any = [];
        for (const item of data.results) {
            if (item['psComputerName'] !== undefined) {
                delete item['psComputerName'];
            }

            if (item.hasOwnProperty('returnValue')) {
                response = { ...response, ...item };
            }

            if (item.hasOwnProperty('outParameters')) {
                for (const parameter of item.outParameters) {
                    response[parameter.name] = parameter.value;
                }
            }

            // aggregate the same parameter name object into single array.
            if (item.hasOwnProperty('itemValue')) {
                if (item.itemValue.length > 0) {
                    const array: any[] = item.itemValue;
                    array.forEach(value => results.push(PowerShellAlternate.cleanCimInstance(value)));
                } else {
                    results.push(PowerShellAlternate.cleanCimInstance(item.itemValue));
                }
            }
        }

        if (results.length > 0) {
            response.results = results;
        }

        return response;
    }

    /**
     * Clean the cim instance object to remove schema/qualifier data.
     *
     * @param data The instance object from PowerShell.
     */
    private static cleanCimInstance(data: any): any {
        if (!data) {
            return data;
        }

        if (data['psComputerName'] !== undefined) {
            delete data['psComputerName'];
        }

        if (data['cimClass']) {
            delete data['cimClass'];
        }

        if (data['cimInstanceProperties']) {
            delete data['cimInstanceProperties'];
        }

        if (data['cimSystemProperties']) {
            const props = data['cimSystemProperties'];
            data['_Namespace'] = props['namespace'];
            data['_ServerName'] = props['serverName'];
            data['_ClassName'] = props['className'];
            data['_Path'] = props['path'];
            delete data['cimSystemProperties'];
        }

        return data;
    }
}
